/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';

export type DeleteM2MClientMutationConfig = {
  onSuccess?: (data: void, variables: string) => void;
  onError?: (error: unknown, variables: string) => void;
  onSettled?: (data: unknown, error: unknown, variables: unknown) => void;
};

/**
 * Hook for deleting an M2M client by ID through the admin OAuth clients API.
 *
 * @param config.onSuccess - Called after successful M2M client deletion
 * @param config.onError - Called if deletion fails
 * @param config.onSettled - Called after mutation completes (success or failure)
 *
 * @returns React Query mutation result for deleting M2M clients
 */
export function useDeleteM2MClientMutation({ onSuccess, onError, onSettled }: DeleteM2MClientMutationConfig) {
  const queryClient = useQueryClient();

  const { mutate, isError, error, isPending } = useMutation<void, ApiError, string>({
    mutationFn: async (clientId: string) => {
      await mgmtPlaneAPIClient.adminOauthClients.deleteAdminOauthClient({
        clientId,
      });
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['m2mClients'] });
      onSuccess?.(data, variables);
    },
    onError,
    onSettled,
  });

  return { mutate, isError, error, isPending };
}
